import React, { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import {
  RecruitmentCriterion,
  RecruitmentSettings,
  staffRecruitmentService,
} from '../services/api';

const RecruitmentCriteria: React.FC = () => {
  const [criteria, setCriteria] = useState<RecruitmentCriterion[]>([]);
  const [settings, setSettings] = useState<RecruitmentSettings>({});
  const [loading, setLoading] = useState(true);
  const [savingCriteria, setSavingCriteria] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        staffRecruitmentService.listCriteria(),
        staffRecruitmentService.getSettings(),
      ]);
      setCriteria(c);
      setSettings(s);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'خطا در دریافت اطلاعات' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalWeight = useMemo(() => criteria.reduce((sum, c) => sum + (c.weight || 0), 0), [criteria]);
  const weightValid = totalWeight === 100;

  const updateCriterion = (id: string, patch: Partial<RecruitmentCriterion>) => {
    setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const saveCriteria = async () => {
    if (!weightValid) return;
    setSavingCriteria(true);
    setMessage(null);
    try {
      const saved = await staffRecruitmentService.saveCriteria(
        criteria.map((c) => ({ id: c.id, title: c.title, guideQuestion: c.guideQuestion, weight: c.weight })),
      );
      setCriteria(saved);
      setMessage({ type: 'success', text: 'وزن شاخص‌ها ذخیره شد.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'ذخیره وزن‌ها ناموفق بود.' });
    } finally {
      setSavingCriteria(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setMessage(null);
    try {
      const saved = await staffRecruitmentService.saveSettings(settings);
      setSettings(saved);
      setMessage({ type: 'success', text: 'تنظیمات فرم ذخیره شد.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'ذخیره تنظیمات ناموفق بود.' });
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">در حال بارگذاری...</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">شاخص‌های ارزیابی استخدام</h1>
        <p className="mt-1 text-sm text-gray-600">
          وزن هر شاخص را بر اساس اهمیتش برای این موقعیت شغلی تنظیم کنید. جمع وزن‌ها باید دقیقاً ۱۰۰٪ باشد؛
          همین شاخص‌ها و وزن‌ها مبنای ارزیابی هوش مصنوعی هر متقاضی خواهند بود.
        </p>
      </div>

      {message ? (
        <div
          className={`rounded-xl px-4 py-3 text-sm font-semibold ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <span
            className={`rounded-full px-3 py-1 text-sm font-bold ${
              weightValid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}
          >
            جمع وزن‌ها: {totalWeight.toLocaleString('fa-IR')}٪ {weightValid ? '' : '(باید ۱۰۰٪ باشد)'}
          </span>
          <button
            type="button"
            onClick={saveCriteria}
            disabled={!weightValid || savingCriteria}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {savingCriteria ? 'در حال ذخیره...' : 'ذخیره همه'}
          </button>
        </div>

        <div className="space-y-3">
          {criteria.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-200 p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label className="mb-1 block text-xs font-semibold text-gray-600">عنوان شاخص</label>
                  <input
                    value={c.title}
                    onChange={(e) => updateCriterion(c.id, { title: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-semibold text-gray-600">وزن (٪)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={c.weight}
                    onChange={(e) => updateCriterion(c.id, { weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                    className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-2">
                <label className="mb-1 block text-xs font-semibold text-gray-600">راهنمای ارزیابی (ورودی پرامپت هوش مصنوعی)</label>
                <textarea
                  rows={2}
                  value={c.guideQuestion}
                  onChange={(e) => updateCriterion(c.id, { guideQuestion: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">تنظیمات سراسری فرم</h2>
          <button
            type="button"
            onClick={saveSettings}
            disabled={savingSettings}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {savingSettings ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">متن معرفی (برای متقاضی)</label>
            <textarea
              rows={3}
              value={settings.introText || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, introText: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">یادداشت حریم‌خصوصی (برای متقاضی)</label>
            <textarea
              rows={2}
              value={settings.privacyNote || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, privacyNote: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">نشانه‌های مثبت (هر خط یک مورد — ورودی هوش مصنوعی)</label>
            <textarea
              rows={4}
              value={settings.positiveSignals || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, positiveSignals: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              مواردی که باید در مصاحبه بیشتر بررسی شوند (هر خط یک مورد — ورودی هوش مصنوعی)
            </label>
            <textarea
              rows={4}
              value={settings.reviewFlags || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, reviewFlags: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">تذکر داوری (ورودی هوش مصنوعی)</label>
            <textarea
              rows={2}
              value={settings.scoringNote || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, scoringNote: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentCriteria;
